import * as z from "zod/v4";
import type { SignalGraphEntry, SignalGraphSourceId } from "./signal-graph";

export const claimExtractionFrequencies = [
  "every_15_minutes",
  "hourly",
  "every_6_hours",
  "daily",
  "weekly",
  "manual",
] as const;

export type ClaimExtractionFrequency = typeof claimExtractionFrequencies[number];

export type WorkflowSourceNode = {
  id: string;
  kind: "source";
  sourceId: string;
  label: string;
  claimExtractionSupported: boolean;
  enabled: boolean;
  claimExtractionFrequency: ClaimExtractionFrequency;
  position: { x: number; y: number };
};

export type WorkflowCanvasInput = {
  nodes: WorkflowSourceNode[];
};

export type WorkflowCanvas = WorkflowCanvasInput & {
  version: number;
  updatedAt: string | null;
};

const workflowSourceNodeSchema = z.object({
  id: z.string().trim().startsWith("workflow-node-").max(200),
  kind: z.literal("source"),
  sourceId: z.string().trim().min(1).max(160),
  label: z.string().trim().min(1).max(160),
  claimExtractionSupported: z.boolean(),
  enabled: z.boolean(),
  claimExtractionFrequency: z.enum(claimExtractionFrequencies),
  position: z.object({
    x: z.number().finite().min(0).max(5_000),
    y: z.number().finite().min(0).max(5_000),
  }),
});

export const workflowCanvasInputSchema = z.object({
  nodes: z.array(workflowSourceNodeSchema).max(64),
}).superRefine((input, context) => {
  const sourceIds = new Set<string>();
  input.nodes.forEach((node, index) => {
    if (sourceIds.has(node.sourceId)) {
      context.addIssue({
        code: "custom",
        message: "Each source can appear only once on the workflow canvas.",
        path: ["nodes", index, "sourceId"],
      });
    }
    sourceIds.add(node.sourceId);
  });
});

export function emptyWorkflowCanvas(): WorkflowCanvas {
  return { version: 0, updatedAt: null, nodes: [] };
}

export const claimExtractionFrequencyLabels: Record<ClaimExtractionFrequency, string> = {
  every_15_minutes: "Every 15 minutes",
  hourly: "Hourly",
  every_6_hours: "Every 6 hours",
  daily: "Daily",
  weekly: "Weekly",
  manual: "Manual only",
};

const frequencyMilliseconds: Record<Exclude<ClaimExtractionFrequency, "manual">, number> = {
  every_15_minutes: 15 * 60_000,
  hourly: 60 * 60_000,
  every_6_hours: 6 * 60 * 60_000,
  daily: 24 * 60 * 60_000,
  weekly: 7 * 24 * 60 * 60_000,
};

export function workflowSourceIsDue(
  canvas: WorkflowCanvas,
  sourceId: SignalGraphSourceId,
  entries: SignalGraphEntry[],
  now = new Date().toISOString(),
) {
  const node = canvas.nodes.find((candidate) => candidate.sourceId === sourceId);
  if (!node) return true;
  if (!node.enabled || !node.claimExtractionSupported || node.claimExtractionFrequency === "manual") {
    return false;
  }
  const latest = entries
    .filter((entry) => entry.sourceId === sourceId)
    .map((entry) => entry.packet?.completedAt ?? entry.updatedAt ?? entry.discoveredAt)
    .sort((left, right) => right.localeCompare(left))[0];
  if (!latest) return true;
  const elapsed = Date.parse(now) - Date.parse(latest);
  if (!Number.isFinite(elapsed)) return true;
  return elapsed >= frequencyMilliseconds[node.claimExtractionFrequency];
}
